import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '../../app/store.js';
import { layerApi } from '../../services/layerApi.js';
import { extentRequested, type PendingExtent } from '../slices/mapSlice.js';
import { toPendingExtent } from './toPendingExtent.js';

/**
 * Frame a layer's own extent.
 *
 * The extent comes from the WMS capabilities via the cached registry, never
 * from the catalog: 189 of 193 catalog records carry a malformed bounding box,
 * and using one would fling the map into open ocean.
 *
 * The layer's scale window goes with it, so this lands somewhere the layer can
 * actually draw. Framing the extent alone puts `ogi:sections` at the statewide
 * view its bounding box describes, which is four zoom levels above the scale
 * where the server will render a single section line.
 */
export const zoomToLayer = createAsyncThunk<
  PendingExtent | null,
  string,
  { state: RootState; dispatch: AppDispatch }
>('map/zoomToLayer', async (layerName, thunk) => {
  const registry = await thunk
    .dispatch(layerApi.endpoints.getLayerRegistry.initiate())
    .unwrap();

  const entry = registry.find((l) => l.name === layerName);
  const target = entry ? toPendingExtent(entry) : null;
  if (!target) return null;

  thunk.dispatch(extentRequested(target));
  return target;
});
